<script setup lang="ts">
// PNG-based logo for TzCloud. Two call styles:
//   <LogoMark size="lg" />                  // preset height (sm/lg)
//   <LogoMark height="6rem" />              // any CSS length
//   <LogoMark class="h-9 ..." />            // pass Tailwind class
// Width is always derived from the source aspect ratio so the wordmark
// never stretches. Source files live under web/public/ —
// logo-{black,white}{,-lg}.png and mark-{black,white}.png.
//
// The image height is set with an inline style (not h-full on a
// wrapper) so the height is decoupled from the parent content size
// and the wordmark never collapses to its natural height. The root
// element is inline-flex so it shrinks to the image and respects
// caller-supplied utility classes (h-9, mx-auto, …) on the wrapper.
//
// Tailwind's preflight forces display:block and max-width:100% on
// every <img>. We override max-width:none inline so the intrinsic
// aspect ratio is preserved (width:auto then resolves to the natural
// width that matches the chosen height).
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    size?: 'sm' | 'lg';
    height?: string; // any CSS length
    variant?: 'wordmark' | 'mark';
  }>(),
  { size: 'sm', height: undefined, variant: 'wordmark' },
);

const suffix = computed(() => {
  if (props.variant === 'mark') return '-mark';
  return props.size === 'lg' ? '-lg' : '';
});

const resolvedHeight = computed(() => {
  if (props.height) return props.height;
  // 3rem = 48px. With the cropped 394×73 wordmark (aspect 5.397)
  // the rendered width is 48×5.397 ≈ 259px — comfortably centered in
  // the 384px login card with breathing room top and bottom.
  return props.size === 'lg' ? '3rem' /* 48px, balanced for login */ : '1.75rem' /* h-7 */;
});

const imgStyle = computed(() => ({
  height: resolvedHeight.value,
  width: 'auto',
  maxWidth: 'none',
}));
</script>

<template>
  <span class="inline-flex items-center shrink-0">
    <img
      :src="`/admin/logo-black${suffix}.png`"
      alt="TzCloud"
      :style="imgStyle"
      class="object-contain dark:hidden"
    />
    <img
      :src="`/admin/logo-white${suffix}.png`"
      alt="TzCloud"
      :style="imgStyle"
      class="object-contain hidden dark:block"
    />
  </span>
</template>